import React, {
  useContext,
  useEffect,
  useMemo,
  Dispatch,
  SetStateAction,
} from "react";
import { AxiosError } from "axios";
import { useQuery } from "react-query";
import { useParams, useRouteMatch } from "react-router-dom";

import { PermissionLevel, UserPermission } from "fe-shared/src/types/domain";
import {
  hasAnyPermission,
  hasPermission,
} from "fe-shared/src/helpers/permissions";
import { ApiError } from "fe-shared/src/types/common";
import { Transfer } from "fe-shared-transactions/src/types";

import { PERMISSIONS, TRANSFERS_QUERY_KEY } from "consts";

import UserContext from "contexts/userContext";
import TransferDetails from "./TransferDetails";
import * as api from "./modules/api";

interface Props {
  setLastOpenedTransfersNumber: Dispatch<SetStateAction<string | null>>;
}

const TransferDetailsView = ({
  setLastOpenedTransfersNumber,
  ...rest
}: Props) => {
  const currentUser = useContext(UserContext);
  const { transferNumber } = useParams<{ transferNumber: string }>();
  const { path } = useRouteMatch();
  const returnUrl = useMemo(() => path.replace("/:transferNumber", ""), [path]);

  useEffect(() => {
    if (transferNumber) {
      setLastOpenedTransfersNumber(transferNumber);
    }
  }, [setLastOpenedTransfersNumber, transferNumber]);

  const hasSidebar = hasAnyPermission(currentUser?.permissions, [
    {
      name: PERMISSIONS.transferHistory,
      level: PermissionLevel.Read,
    },
    {
      name: PERMISSIONS.transferNotes,
      level: PermissionLevel.Read,
    },
  ]);

  const isPermitted = (permission: UserPermission) =>
    hasPermission(currentUser?.permissions, permission);

  const canExportHistory = isPermitted({
    name: PERMISSIONS.transferHistoryExport,
    level: PermissionLevel.Full,
  });

  const canViewNotes = isPermitted({
    name: PERMISSIONS.transferNotes,
    level: PermissionLevel.Read,
  });

  const canAddNote = isPermitted({
    name: PERMISSIONS.transferAddNote,
    level: PermissionLevel.Full,
  });

  const canExportNotes = isPermitted({
    name: PERMISSIONS.transferNotesExport,
    level: PermissionLevel.Full,
  });

  const canViewTransferDetails = isPermitted({
    name: PERMISSIONS.transfersDetails,
    level: PermissionLevel.Read,
  });

  const canCancelTransfer = isPermitted({
    name: PERMISSIONS.transferCancel,
    level: PermissionLevel.Full,
  });

  const canInvalidateTransfer = isPermitted({
    name: PERMISSIONS.transferInvalidate,
    level: PermissionLevel.Full,
  });

  const canApproveTransfer = isPermitted({
    name: PERMISSIONS.transferApprove,
    level: PermissionLevel.Full,
  });

  const canAssignTransfer = isPermitted({
    name: PERMISSIONS.transferAssign,
    level: PermissionLevel.Full,
  });

  const canEditTransfer = isPermitted({
    name: PERMISSIONS.transfersDetails,
    level: PermissionLevel.Full,
  });

  const canSeeLinkedBatches = isPermitted({
    name: PERMISSIONS.transactionSeeTraceability,
    level: PermissionLevel.Read,
  });

  const transferQueryKey = [TRANSFERS_QUERY_KEY, transferNumber];

  const transfer = useQuery<Transfer, AxiosError<ApiError>>(
    transferQueryKey,
    api.getTransferInfo,
    {
      keepPreviousData: true,
      enabled: canViewTransferDetails,
    }
  );

  return (
    <TransferDetails
      hasSidebar={hasSidebar}
      canExportHistory={canExportHistory}
      canViewNotes={canViewNotes}
      canAddNote={canAddNote}
      canExportNotes={canExportNotes}
      canEditTransfer={canEditTransfer}
      canCancelTransfer={canCancelTransfer}
      canInvalidateTransfer={canInvalidateTransfer}
      canApproveTransfer={canApproveTransfer}
      canAssignTransfer={canAssignTransfer}
      canSeeLinkedBatches={canSeeLinkedBatches}
      userEmail={currentUser?.email}
      transferQueryKey={transferQueryKey}
      returnUrl={returnUrl}
      transfer={transfer}
      {...rest}
    />
  );
};

export default TransferDetailsView;
